import {Table, TableCaption} from '@/base/ui/table'
import {cn} from '@/lib/utils'
import {
  ColumnFiltersState,
  ColumnSort,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {cva} from 'class-variance-authority'
import React, {useEffect, useMemo, useState} from 'react'
import {useRowSelection} from '../../hooks/useRowSelection'
import {IWuTableColumnDef} from '../../types/IWuTableColumnDef'
import {WuTableBody} from '../../ui/_body'
import {WuTableHeader} from '../../ui/_header'

export interface IWuTableProps<T>
  extends React.TableHTMLAttributes<HTMLTableElement> {
  data: T[]
  columns: IWuTableColumnDef<T>[]
  variant?: 'striped' | 'unstyled' | 'bordered'
  size?: 'compact' | 'default'
  caption?: string
  sort?: {
    enabled?: boolean
    initial?: ColumnSort[]
  }
  filterText?: string
  pagination?: {
    pageIndex: number
    pageSize: number
  }
  rowSelection?: {
    isEnabled: boolean
    selectedRows: T[]
    onRowSelect: React.Dispatch<React.SetStateAction<T[]>>
    rowUniqueKey: string
  }
  HeaderAction?: React.ReactElement
  NoDataContent?: React.ReactNode
}

export const WuTable = <T,>({
  data,
  columns,
  caption,
  variant = 'unstyled',
  sort,
  filterText,
  size = 'default',
  pagination,
  rowSelection,
  HeaderAction,
  NoDataContent,
  ...rest
}: IWuTableProps<T>): React.JSX.Element => {
  const [sorting, setSorting] = useState<ColumnSort[]>(sort?.initial || [])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const selectedRowCount = rowSelection?.selectedRows?.length ?? 0

  const [currentPage, setCurrentPage] = React.useState(
    pagination?.pageIndex ?? 0,
  )

  const {finalColumns} = useRowSelection({
    columns,
    rowSelection,
  })

  const sortableColumns = useMemo(
    () =>
      finalColumns.filter(col => col.enableSorting).map(col => col.accessorKey),
    [finalColumns],
  )

  const filterableColumns = useMemo(
    () =>
      finalColumns.filter(col => col.filterable).map(col => col.accessorKey),
    [finalColumns],
  )

  const table = useReactTable({
    data,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: rowSelection?.isEnabled,
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: pagination?.pageSize ?? 100,
        pageIndex: pagination?.pageIndex ?? 0,
      },
    },
    state: {sorting, columnFilters, ...(pagination ? {pagination} : {})},
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  })

  useEffect(() => {
    filterableColumns.forEach(key => {
      table.getColumn(key)?.setFilterValue(filterText)
    })
  }, [filterText, filterableColumns, table])

  useEffect(() => {
    if (pagination && pagination?.pageIndex !== currentPage) {
      rowSelection?.onRowSelect(() => [])
      table.resetRowSelection()
      setCurrentPage(pagination.pageIndex)
    }
  }, [pagination?.pageIndex])

  return (
    <div className="wu-table-container">
      <Table
        {...rest}
        className={cn(variantClasses({variant}), 'table-fixed', rest.className)}
      >
        {caption && (
          <TableCaption className="font-light">{caption}</TableCaption>
        )}

        <WuTableHeader
          table={table}
          sortableColumns={sortableColumns}
          size={size}
          HeaderAction={HeaderAction}
          isRowSelected={selectedRowCount > 0}
        />

        <WuTableBody table={table} size={size} NoDataContent={NoDataContent} />
      </Table>
    </div>
  )
}

const variantClasses = cva('', {
  variants: {
    variant: {
      striped: '[&_tr:nth-child(even)]:bg-zinc-50',
      unstyled: '',
      bordered: 'border-collapse border [&_th]:border [&_td]:border',
    },
  },
})

WuTable.displayName = 'WuTable'
